import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UseBreakpoints } from '@my2n/use-breakpoints';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../config/consts';
import { SITE_SERVICE } from '../../config/sites';
import { useDataGrid } from '../../helpers/dataGrid';
import { isFetchingMessages } from '../../modules/localization/selectors';
import * as siteSelectors from '../../modules/sites/selectors';
import { getUserName, mayRoleLogIn } from '../../modules/user/utils';
import { useAppContext } from '../../pages/App/AppContextProvider';
import ApartmentNameChip from '../ApartmentNameChip';
import DataGrid from '../DataGrid/DataGrid';
import NameColumn from '../DataGrid/NameColumn';
import { DataGridRowSkeleton } from '../DataGridRowSkeleton/DataGridRowSkeleton';
import Link from '../Link';
import UserAccessGroupsModal from '../UserAccessGroupsModal';
import UserAccessSettingsStatus from '../UserAccessSettingsStatus';
import UserApartmentModal from '../UserApartmentModal';
import UserEmail from '../UserEmail';
import UserIcon from '../UserIcon';
import { UserRoleName } from '../UserRoleName';
import messages from './messages';
import SiteUsersRowMenu from './SiteUsersRowMenu/SiteUsersRowMenu';

const SiteUsersDataGrid = ({
  companyId,
  data,
  didInvalid,
  filter,
  isFetching,
  onLoadUsers,
  order,
  page,
  resultsFiltered,
  resultsTotal,
  rowsPerPage,
  siteId,
}) => {
  const { onOrderBy, onRowsPerPageChange, onSelectPage } = useDataGrid(
    onLoadUsers,
    {
      companyId,
      filter,
      order,
      page,
      rowsPerPage,
      siteId,
    },
    didInvalid,
    isFetching
  );

  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { upLg, upSm } = UseBreakpoints();
  const { feSiteRoles } = useAppContext();
  const isLocalizationLoaded = useSelector(isFetchingMessages);
  const siteData = useSelector(siteSelectors.getSiteData);

  const [userApartmentModalOpen, setUserApartmentModalOpen] = useState(false);

  const columns = useMemo(
    () => [
      {
        disableColumnMenu: true,
        field: 'lastName',
        flex: 1,
        headerName: formatMessage(messages.siteUsersDataGridColumnsName),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && (
              <NameColumn
                isTimeLimitedAccess={!!(cell?.value?.validFrom || cell?.value?.validTo)}
                iconComponent={<UserIcon userRole={cell.value.role} />}
                name={getUserName({ firstName: cell.value.firstName, lastName: cell.value.lastName }, true)}
              />
            )}
          </DataGridRowSkeleton>
        ),
        sortable: true,
      },
      {
        disableColumnMenu: true,
        field: 'apartment',
        flex: 1,
        headerName: formatMessage(messages.siteUsersDataGridColumnsApartment),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && (
              <ApartmentNameChip
                count={cell.value.apartments.length}
                name={cell.value.apartments?.[0]?.name}
                number={cell.value.apartments?.[0]?.number}
              />
            )}
          </DataGridRowSkeleton>
        ),
        sortable: true,
      },
      {
        disableColumnMenu: true,
        field: 'email',
        flex: 1,
        headerName: formatMessage(messages.siteUsersDataGridColumnsEmail),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && (
              <UserEmail
                email={cell.value.email}
                isEnabled={cell.value.enabled}
                userRoleCanLogin={mayRoleLogIn(cell.value.role)}
              />
            )}
          </DataGridRowSkeleton>
        ),
        sortable: true,
      },
      {
        disableColumnMenu: true,
        field: 'role',
        flex: 1,
        headerName: formatMessage(messages.siteUsersDataGridColumnsRole),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && <UserRoleName userRole={cell.value.role} />}
          </DataGridRowSkeleton>
        ),
        sortable: true,
      },
      {
        disableColumnMenu: true,
        field: 'access',
        flex: 1,
        headerName: formatMessage(messages.siteUsersDataGridColumnsAccess),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && (
              <UserAccessSettingsStatus
                apartmentAccess={cell?.value?.apartmentAccess}
                credentials={cell?.value?.credentials}
                isTimeLimitedAccess={!!(cell?.value?.validFrom || cell?.value?.validTo)}
                isSiteBleActive={siteData?.services[SITE_SERVICE.ACCESS_CONTROL].bluetoothEnabled}
              />
            )}
          </DataGridRowSkeleton>
        ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'rowMenu',
        headerName: '',
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && (
              <SiteUsersRowMenu
                companyId={companyId}
                setUserApartmentModalOpen={() => setUserApartmentModalOpen(true)}
                siteId={siteId}
                user={cell.value}
              />
            )}
          </DataGridRowSkeleton>
        ),
        sortable: false,
        width: 65,
      },
    ],
    [isFetching, upSm, upLg, isLocalizationLoaded]
  );

  const rows = useMemo(
    () =>
      data.map((user) => ({
        access: user,
        apartment: user,
        email: user,
        id: user.id,
        isClickable: true,
        lastName: user,
        role: user,
        rowMenu: user,
      })),
    [data]
  );

  return (
    <>
      <DataGrid
        columns={columns}
        columnVisibilityModel={{
          access: upSm,
          apartment: upLg,
          email: upLg,
        }}
        disableRowSelectionOnClick
        hideFooterPagination={isFetching}
        loading={isFetching}
        onCellClick={(params) => {
          if (params.field !== 'rowMenu') {
            if (params?.value?.id && siteId && companyId) {
              navigate(
                PATHS.SITE_USER_DETAIL.replace(':companyId', companyId)
                  .replace(':siteId', siteId)
                  .replace(':userId', params.value?.id)
              );
            }
          }
        }}
        onOrderBy={onOrderBy}
        onRowsPerPageChange={onRowsPerPageChange}
        onSelectPage={onSelectPage}
        order={order}
        page={page}
        resultsFiltered={resultsFiltered}
        resultsTotal={resultsTotal}
        rows={rows}
        rowsPerPage={rowsPerPage}
      />
      {siteId !== null && feSiteRoles.isCompanyAdmin && (
        <Box sx={{ bgcolor: 'white', p: 2 }}>
          <Typography variant="body2">
            <FormattedMessage
              {...messages.siteUsersDataGridCompanyAdmins}
              values={{
                link: (
                  <Link to={PATHS.COMPANY_ADMIN.replace(':companyId', companyId)}>
                    <FormattedMessage {...messages.siteUsersDataGridCompanyAdminsLink} />
                  </Link>
                ),
              }}
            />
          </Typography>
        </Box>
      )}
      <UserAccessGroupsModal />
      <UserApartmentModal onClose={() => setUserApartmentModalOpen(false)} open={userApartmentModalOpen} />
    </>
  );
};

SiteUsersDataGrid.propTypes = {
  companyId: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      companies: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
          roles: PropTypes.arrayOf(PropTypes.string),
          sites: PropTypes.arrayOf(
            PropTypes.shape({
              apartments: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.number,
                  name: PropTypes.string,
                  number: PropTypes.string,
                  roles: PropTypes.arrayOf(PropTypes.string),
                })
              ),
              id: PropTypes.number,
              name: PropTypes.string,
              roles: PropTypes.arrayOf(PropTypes.string),
            })
          ),
        })
      ),
      credentials: PropTypes.arrayOf(PropTypes.string),
      email: PropTypes.string,
      enabled: PropTypes.bool,
      firstName: PropTypes.string,
      id: PropTypes.number,
      lastName: PropTypes.string,
      role: PropTypes.oneOfType([
        PropTypes.shape({
          isCompanyAdmin: PropTypes.bool,
          isSiteAdmin: PropTypes.bool,
          isSiteAdminPayment: PropTypes.bool,
        }),
        PropTypes.string,
      ]),
    })
  ).isRequired,
  didInvalid: PropTypes.bool.isRequired,
  filter: PropTypes.shape({ fulltext: PropTypes.string }).isRequired,
  isFetching: PropTypes.bool.isRequired,
  onLoadUsers: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  resultsFiltered: PropTypes.number.isRequired,
  resultsTotal: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
};

export default SiteUsersDataGrid;
